<script setup lang="ts">
import BreadCrumb from "@/components/BreadCrumb.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import { useAuth } from "@/composables/useAuth";
import useServices from "@/composables/useServices";
import { SERVICES } from "@/config/services";
import { AbilityEntityType, type Ability } from "@/interfaces/ability";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import useUser from "../composables/useUser";
import useUserAbilities from "../composables/useUserAbilities";

const route = useRoute();
const queryClient = useQueryClient();
const toast = useToast();

const { user, isLoading: loadingUser } = useUser({
  id: route.params.id.toString(),
  immediate: true,
});

const companyId = computed(() => user.value?.companyId);

const { userAbilities, isLoading, postUserAbilityAsync } = useUserAbilities({
  params: { userId: route.params.id.toString(), companyId },
});

const { abilities: actorAbilities } = useAuth();
const { getService } = useServices();

const saveSuccess = ref(false);

const filteredAbilities = computed(() => {
  if (actorAbilities.value?.some((a) => a.name === "*")) return userAbilities.value;
  return userAbilities.value.filter((ability) =>
    actorAbilities.value.some(
      (a) => a.name === ability.name && a.entityType === ability.entityType,
    ),
  );
});

// Derivado del registry por la `ability` que declara cada servicio, en vez de un
// mapa paralelo: agregar un servicio no debe obligar a tocar esta pantalla.
const SERVICE_ICONS: Record<string, string> = Object.fromEntries(
  Object.values(SERVICES).map((service) => [service.ability, service.icon]),
);

const MODULE_ICONS: Record<string, string> = {
  Empresa: "business",
  Usuario: "person",
  Reservas: "travel_explore",
  CEF: "receipt_long",
  Aprobaciones: "approval_delegation",
  Políticas: "policy",
  Roles: "shield_person",
  Grupo: "group",
  Corporativo: "corporate_fare",
  Permisos: "key",
  Subscription: "subscriptions",
  "Cartas garantía": "description",
  General: "settings",
  "*": "admin_panel_settings",
};

const serviceAbilities = computed(() =>
  filteredAbilities.value.filter(
    (a) =>
      a.name.startsWith("create-") &&
      a.entityType === AbilityEntityType.AppModelsRequest,
  ),
);

const nonServiceAbilities = computed(() =>
  filteredAbilities.value.filter(
    (a) =>
      !(
        a.name.startsWith("create-") &&
        a.entityType === AbilityEntityType.AppModelsRequest
      ),
  ),
);

const serviceLabel = (abilityName: string) => {
  const serviceName = abilityName.replace("create-", "");
  const found = getService(serviceName)?.displayName;
  if (found) return found;
  return serviceName.charAt(0).toUpperCase() + serviceName.slice(1);
};

const activeCount = computed(() => nonServiceAbilities.value.filter((a) => a.checked).length);
const totalCount = computed(() => nonServiceAbilities.value.length);
const progressPct = computed(() =>
  totalCount.value ? Math.round((activeCount.value / totalCount.value) * 100) : 0
);

// El backend nombra este módulo "CEF"; en UI se muestra como "Itinerarios"
const MODULE_DISPLAY_NAMES: Record<string, string> = { CEF: "Itinerarios" };
const moduleDisplayName = (name: string) => MODULE_DISPLAY_NAMES[name] ?? name;

const moduleData = computed(() => {
  const map = new Map<string, Ability[]>();
  for (const ability of nonServiceAbilities.value) {
    if (!map.has(ability.module)) map.set(ability.module, []);
    map.get(ability.module)!.push(ability);
  }
  return Array.from(map.entries()).map(([module, abilities]) => ({
    module,
    icon: MODULE_ICONS[module] ?? "lock",
    abilities,
    activeCount: abilities.filter((a) => a.checked).length,
  }));
});

const closedModules = ref<string[]>([]);
const searchQuery = ref("");
const searchOpen = ref(false);
const searchInputRef = ref<HTMLInputElement | null>(null);

const filteredModuleData = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return moduleData.value;
  return moduleData.value
    .map((section) => ({
      ...section,
      abilities: section.abilities.filter((a) =>
        (a.label || a.title || a.name).toLowerCase().includes(q) ||
        section.module.toLowerCase().includes(q)
      ),
    }))
    .filter((section) => section.abilities.length > 0)
    .map((section) => ({ ...section, activeCount: section.abilities.filter((a) => a.checked).length }));
});

const totalMatches = computed(() => filteredModuleData.value.reduce((sum, s) => sum + s.abilities.length, 0));

const isModuleOpen = (module: string) =>
  searchQuery.value.trim() ? true : !closedModules.value.includes(module);

const toggleModule = (module: string) => {
  const idx = closedModules.value.indexOf(module);
  if (idx >= 0) closedModules.value.splice(idx, 1);
  else closedModules.value.push(module);
};

const toggleSearch = async () => {
  searchOpen.value = !searchOpen.value;
  if (searchOpen.value) {
    await nextTick();
    searchInputRef.value?.focus();
  } else {
    searchQuery.value = "";
  }
};

const toggleAllInModule = (abilities: Ability[], activeCount: number) => {
  const shouldActivate = activeCount < abilities.length;
  abilities.forEach((a) => { a.checked = shouldActivate; });
};

const togglePermission = (id: number) => {
  const a = userAbilities.value.find((x) => x.id === id);
  if (!a) return;
  a.checked = !a.checked;
  if (!a.name.includes("-")) {
    userAbilities.value.forEach((item) => {
      if (item.name.includes(a.name) && item.name.includes("-") && item.entityType === a.entityType)
        item.checked = a.checked;
    });
  } else {
    userAbilities.value.forEach((item) => {
      if (
        !item.name.includes("-") &&
        item.entityType === a.entityType &&
        item.name.toLocaleLowerCase() === a.name.split("-")[0].toLocaleLowerCase() &&
        a.checked
      ) item.checked = true;
    });
  }
};

const handleSave = async () => {
  const userAbilitiesToUpdate = userAbilities.value
    .filter((item) => item.checked)
    .map((item) => item.id);

  await postUserAbilityAsync({
    userId: route.params.id.toString(),
    abilities: userAbilitiesToUpdate,
    toast,
  });

  await queryClient.invalidateQueries(["userAbilities", route.params.id.toString(), companyId.value]);
  saveSuccess.value = true;
  setTimeout(() => { saveSuccess.value = false; }, 2000);
};

const roleLabel = (role: string) => {
  const labels: Record<string, string> = {
    master: "Master",
    travel_manager: "Travel Manager",
    supervisor: "Supervisor",
    traveler: "Viajero",
  };
  return labels[role] ?? role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
};

const pages = ref<{ name: string; to: string; current: boolean }[]>([]);

watch(
  user,
  (value) => {
    if (value) {
      pages.value = [
        { name: "Usuarios", to: "/users", current: false },
        {
          name: `Permisos de ${value.fullName}`,
          to: `/users/permissions/${route.params.id}`,
          current: true,
        },
      ];
    }
  },
  { immediate: true },
);
</script>

<template>
  <BreadCrumb :pages="pages" v-if="!loadingUser" />

  <div class="my-6 mb-10 mx-auto max-w-6xl px-4 md:px-6">
    <!-- Header -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm mb-5 overflow-hidden">
      <div class="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-2xl bg-[#021F58] flex items-center justify-center flex-shrink-0 text-white text-lg font-bold">
            {{ user?.fullName?.charAt(0)?.toUpperCase() ?? '?' }}
          </div>
          <div>
            <div class="flex items-center gap-2 flex-wrap">
              <h1 v-if="!loadingUser" class="text-base font-bold text-gray-900">{{ user?.fullName }}</h1>
              <div v-else class="h-5 w-40 bg-gray-100 rounded animate-pulse" />
              <span
                v-if="user?.roles?.length"
                class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#021F58]/10 text-[#021F58]"
              >{{ roleLabel(user.roles[0]) }}</span>
            </div>
            <p class="text-xs text-gray-400 mt-0.5">{{ user?.companyName }}</p>
            <div class="flex items-center gap-2 mt-1">
              <span class="text-[10px] text-gray-400">{{ activeCount }}/{{ totalCount }} permisos activos</span>
              <span
                class="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                :class="progressPct === 0 ? 'bg-gray-100 text-gray-400' : progressPct === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-[#021F58]/10 text-[#021F58]'"
              >{{ progressPct }}%</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0">
          <RouterLink
            :to="`/users/${route.params.id}`"
            class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <span class="material-symbols-outlined text-base leading-none">person</span>
            Ver perfil
          </RouterLink>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
            :class="saveSuccess ? 'bg-emerald-500 text-white' : 'bg-[#021F58] text-white hover:bg-[#021F58]/90'"
            @click="handleSave"
          >
            <span class="material-symbols-outlined text-base leading-none">{{ saveSuccess ? 'check' : 'save' }}</span>
            {{ saveSuccess ? 'Guardado' : 'Guardar cambios' }}
          </button>
        </div>
      </div>

      <div class="h-1 bg-gray-100">
        <div
          class="h-full transition-all duration-500"
          :class="progressPct === 100 ? 'bg-emerald-500' : 'bg-[#021F58]'"
          :style="{ width: `${progressPct}%` }"
        />
      </div>
    </div>

    <!-- Servicios disponibles -->
    <div
      v-if="serviceAbilities.length"
      class="bg-white rounded-2xl border border-gray-200 shadow-sm mb-5 overflow-hidden"
    >
      <div class="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <div class="w-7 h-7 rounded-lg bg-[#021F58]/10 flex items-center justify-center">
          <span class="material-symbols-outlined text-sm text-[#021F58] leading-none">travel_explore</span>
        </div>
        <h2 class="text-sm font-semibold text-gray-900">Servicios disponibles</h2>
      </div>
      <div class="flex flex-wrap gap-2 px-5 py-4">
        <button
          v-for="ability in serviceAbilities"
          :key="ability.id"
          type="button"
          class="flex items-center gap-2.5 px-3.5 py-2 rounded-xl border-2 transition-all duration-150"
          :class="ability.checked ? 'border-[#021F58] bg-[#021F58]/5' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'"
          @click="ability.checked = !ability.checked"
        >
          <div
            class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
            :class="ability.checked ? 'bg-[#021F58] text-white' : 'bg-gray-100 text-gray-400'"
          >
            <span class="material-symbols-outlined text-base leading-none">{{ SERVICE_ICONS[ability.name] ?? 'travel_explore' }}</span>
          </div>
          <span class="text-sm font-medium" :class="ability.checked ? 'text-[#021F58]' : 'text-gray-600'">
            {{ serviceLabel(ability.name) }}
          </span>
          <div
            class="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
            :class="ability.checked ? 'bg-[#021F58] border-[#021F58]' : 'border-gray-300'"
          >
            <span v-if="ability.checked" class="material-symbols-outlined text-[8px] text-white leading-none">check</span>
          </div>
        </button>
      </div>
    </div>

    <!-- Permissions — module accordion cards -->
    <div v-if="!isLoading" class="space-y-3">
      <div
        v-if="!filteredModuleData.length"
        class="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center justify-center py-12 text-gray-400"
      >
        <span class="material-symbols-outlined text-3xl mb-2">{{ searchQuery ? 'search_off' : 'lock_open' }}</span>
        <p class="text-sm">{{ searchQuery ? `Sin resultados para "${searchQuery}"` : 'Sin permisos configurados' }}</p>
      </div>

      <div
        v-for="section in filteredModuleData"
        :key="section.module"
        class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
      >
        <!-- Module header -->
        <button
          type="button"
          class="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-gray-50/70 transition-colors"
          @click="toggleModule(section.module)"
        >
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-xl bg-[#021F58]/8 flex items-center justify-center flex-shrink-0">
              <span class="material-symbols-outlined text-[#021F58] leading-none text-[18px]">{{ section.icon }}</span>
            </div>
            <div class="text-left">
              <p class="text-sm font-semibold text-gray-900">{{ moduleDisplayName(section.module) }}</p>
              <p class="text-xs text-gray-400">
                {{ section.activeCount }} / {{ section.abilities.length }} activos
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="hidden sm:block w-28 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full bg-[#021F58] transition-all duration-500"
                :style="{ width: `${section.abilities.length ? (section.activeCount / section.abilities.length) * 100 : 0}%` }"
              />
            </div>
            <span
              class="text-[11px] font-bold px-2 py-0.5 rounded-full min-w-[2rem] text-center"
              :class="section.activeCount === 0 ? 'bg-gray-100 text-gray-400' : section.activeCount === section.abilities.length ? 'bg-emerald-100 text-emerald-700' : 'bg-[#021F58]/10 text-[#021F58]'"
            >{{ section.activeCount }}</span>
            <span
              class="material-symbols-outlined text-gray-400 text-xl leading-none transition-transform duration-200 flex-shrink-0"
              :class="isModuleOpen(section.module) ? 'rotate-180' : ''"
            >expand_more</span>
          </div>
        </button>

        <!-- Abilities list -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div v-if="isModuleOpen(section.module)" class="border-t border-gray-100">
            <!-- Sub-header with toggle all -->
            <div class="px-5 py-2.5 flex items-center justify-between bg-gray-50/50">
              <span class="text-[10px] uppercase tracking-widest font-bold text-gray-400">Permisos</span>
              <button
                type="button"
                class="text-[11px] font-semibold px-2.5 py-1 rounded-lg border transition-colors"
                :class="section.activeCount < section.abilities.length
                  ? 'border-[#021F58]/20 text-[#021F58] bg-[#021F58]/5 hover:bg-[#021F58]/10'
                  : 'border-red-200 text-red-500 bg-red-50 hover:bg-red-100'"
                @click.stop="toggleAllInModule(section.abilities, section.activeCount)"
              >
                {{ section.activeCount < section.abilities.length ? 'Activar todos' : 'Desactivar todos' }}
              </button>
            </div>

            <!-- Permission chips -->
            <div class="px-4 pb-4 pt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              <button
                v-for="ability in section.abilities"
                :key="ability.id"
                type="button"
                class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-150 border-l-2 group"
                :class="ability.checked
                  ? 'border-[#021F58] bg-[#021F58]/5'
                  : 'border-transparent hover:border-gray-200 hover:bg-gray-50'"
                @click="togglePermission(ability.id)"
              >
                <div
                  class="w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-150"
                  :class="ability.checked ? 'bg-[#021F58] border-[#021F58]' : 'border-gray-300 group-hover:border-gray-400'"
                >
                  <span v-if="ability.checked" class="material-symbols-outlined text-[10px] text-white leading-none">check</span>
                </div>
                <span
                  class="text-sm flex-1 transition-colors truncate"
                  :class="ability.checked ? 'text-[#021F58] font-medium' : 'text-gray-600'"
                >{{ ability.label || ability.title }}</span>
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-else class="space-y-3">
      <div
        v-for="i in 4"
        :key="i"
        class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
      >
        <div class="px-5 py-4 flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-gray-100 animate-pulse flex-shrink-0" />
          <div class="flex-1 space-y-1.5">
            <div class="h-4 w-32 bg-gray-100 rounded animate-pulse" />
            <div class="h-3 w-20 bg-gray-100 rounded animate-pulse" />
          </div>
          <div class="w-24 h-1.5 bg-gray-100 rounded-full animate-pulse hidden sm:block" />
        </div>
        <div class="border-t border-gray-100 px-4 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          <div
            v-for="j in 6"
            :key="j"
            class="h-10 rounded-xl bg-gray-100 animate-pulse"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Floating search -->
  <div class="fixed bottom-6 right-6 z-30 flex items-center gap-2">
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-x-4 scale-95"
      enter-to-class="opacity-100 translate-x-0 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-x-0 scale-100"
      leave-to-class="opacity-0 translate-x-4 scale-95"
    >
      <div
        v-if="searchOpen"
        class="flex items-center gap-2 bg-white rounded-2xl border border-gray-200 shadow-lg px-3.5 py-2.5"
      >
        <span class="material-symbols-outlined text-gray-400 text-[18px] leading-none flex-shrink-0">search</span>
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          placeholder="Buscar permiso..."
          class="w-48 text-sm outline-none text-gray-800 placeholder-gray-400 bg-transparent"
          @keydown.escape="toggleSearch"
        />
        <span
          v-if="searchQuery"
          class="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
          :class="totalMatches > 0 ? 'bg-[#021F58]/10 text-[#021F58]' : 'bg-red-50 text-red-500'"
        >{{ totalMatches }}</span>
        <button
          v-if="searchQuery"
          type="button"
          class="text-gray-400 hover:text-gray-600 flex-shrink-0"
          @click="searchQuery = ''"
        >
          <span class="material-symbols-outlined text-[16px] leading-none">close</span>
        </button>
      </div>
    </Transition>

    <button
      type="button"
      class="w-11 h-11 rounded-2xl shadow-lg flex items-center justify-center transition-all duration-200"
      :class="searchOpen ? 'bg-[#021F58] text-white shadow-[#021F58]/30' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800'"
      @click="toggleSearch"
    >
      <span class="material-symbols-outlined text-xl leading-none">{{ searchOpen && !searchQuery ? 'close' : 'search' }}</span>
    </button>
  </div>

  <LoadingModal :when="loadingUser" />
</template>
